/**
 * components/landing/home/_shared.jsx — utilities shared across homepage
 * sections.  Kept tiny so we don't ship extra libs in Phase 2.
 *
 *   useInView(ref)          Reveals content on first scroll into viewport.
 *                            Returns a boolean.  Uses IntersectionObserver.
 *   <AnimatedCounter />      Counts up from 0 to `to` once when visible.
 *   <MockDashboardChrome />  Common frame reused by every dashboard preview
 *                            (sidebar rail + top bar + content slot) so the
 *                            homepage "screenshots" all look like the same
 *                            product family.
 *   <GlowCard />             Slightly heavier variant of GlassCard used for
 *                            hero-sized product previews.
 *   <MiniSparkline />        A pure-SVG sparkline used inside stat rows and
 *                            trading previews — no charting lib required.
 */

import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'

export const useInView = (ref, { rootMargin = '0px 0px -80px 0px' } = {}) => {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === 'undefined') { setVisible(true); return }
    const el = ref.current
    const io = new IntersectionObserver(
      (entries) => { entries.forEach((e) => e.isIntersecting && setVisible(true)) },
      { rootMargin, threshold: 0.12 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [ref, rootMargin])
  return visible
}

export const AnimatedCounter = ({ to, prefix = '', suffix = '', duration = 1600, className = '', format = null }) => {
  const ref = useRef(null)
  const inView = useInView(ref)
  const [n, setN] = useState(0)
  useEffect(() => {
    if (!inView) return
    const start = performance.now()
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3)
      setN(Math.round(to * eased))
      if (p < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [inView, to, duration])
  const display = format ? format(n) : n.toLocaleString()
  return (
    <span ref={ref} className={className}>{prefix}{display}{suffix}</span>
  )
}

export const MockDashboardChrome = ({ children, className = '', innerClass = '' }) => (
  <div className={clsx(
    'relative rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] backdrop-blur-xl',
    'shadow-[0_1px_0_0_rgba(255,255,255,0.05)_inset,0_50px_100px_-40px_rgba(0,0,0,0.9)]',
    'overflow-hidden',
    className,
  )}>
    {/* window chrome */}
    <div className="flex items-center gap-1.5 border-b border-white/5 bg-black/40 px-3 py-2.5">
      <div className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
      <div className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
      <div className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
      <div className="ml-4 flex-1 h-5 rounded-md bg-white/[0.04] flex items-center px-2 text-[10px] font-mono text-zinc-500">
        <span className="opacity-70">app.kalkiai.trade</span>
      </div>
      <div className="h-5 w-16 rounded-md bg-white/[0.03]" />
    </div>
    <div className={clsx('bg-[radial-gradient(75%_50%_at_50%_0%,rgba(255,193,7,0.06),transparent)]', innerClass)}>
      {children}
    </div>
  </div>
)

export const GlowCard = ({ className = '', children }) => (
  <div className={clsx(
    'relative rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-2xl overflow-hidden',
    'shadow-[0_1px_0_0_rgba(255,255,255,0.05)_inset,0_60px_140px_-40px_rgba(0,0,0,0.95),0_0_120px_-30px_rgba(255,193,7,0.35)]',
    className,
  )}>
    <div className="pointer-events-none absolute inset-0 opacity-70"
      style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.10), transparent 70%), radial-gradient(50% 40% at 90% 100%, rgba(16,185,129,0.08), transparent 70%)' }} />
    {children}
  </div>
)

export const MiniSparkline = ({ data = [], stroke = '#FFC107', height = 32, width = 120, filled = true, className = '' }) => {
  if (!data.length) return null
  const min = Math.min(...data), max = Math.max(...data)
  const range = max - min || 1
  const step = width / Math.max(1, data.length - 1)
  const pts = data.map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / range) * height).toFixed(1)}`)
  const line = `M ${pts.join(' L ')}`
  const area = `${line} L ${width},${height} L 0,${height} Z`
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} className={className} preserveAspectRatio="none">
      {filled && <path d={area} fill={stroke} opacity="0.14" />}
      <path d={line} stroke={stroke} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// A cheap pseudo-random walk so faux charts always look plausible without
// requiring a live data source.  Seeded so the SSR output matches the
// client hydration exactly.
export const seededWalk = (seed, len = 40, amp = 20, start = 100) => {
  let s = seed, out = [start]
  for (let i = 1; i < len; i++) {
    s = (s * 9301 + 49297) % 233280
    const delta = ((s / 233280) - 0.5) * amp
    out.push(Math.max(1, out[i - 1] + delta))
  }
  return out
}
